import PaywallModal, { type PaywallTenant } from "./components/PaywallModal";
import SegmentCustomersModal from "./components/SegmentCustomersModal";
import SettlementDrilldownModal from "./components/SettlementDrilldownModal";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Box,
  Button,
  Flex,
  Grid,
  Input,
  Popover,
  Portal,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import YearMonthPicker from "../../../components/YearMonthPicker";
import { ChevronRight } from "lucide-react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  FilePlus,
  Filter,
  Gauge,
  RefreshCcw,
  TrendingUp,
} from "lucide-react";
import SwitchStatCard from "./components/SwitchStatCard";
import {
  ChartLegend,
  DataTable,
  KpiCard,
  MetricRows,
  Panel,
  SectionIntro,
  SectionLabel,
  StatusBadge,
  palette,
} from "./components/ui";
import { money } from "@/hooks/useNumberFormatter";
import { track } from "@/helpers/posthog";
import {
  useAnalyticsCurrent,
  useAnalyticsMonthly,
  useAnalyticsSegmentCustomers,
} from "./hooks/useFetchAnalytics";

/**
 * Presentation for the keys the API returns. Spanish copy and colours live here
 * rather than in the backend or in SQL, so there is one place to restyle or
 * translate the dashboard.
 */
const AGING = {
  not_due: { label: "Sin vencer", color: palette.blue },
  "1_5": { label: "1-5 dias", color: palette.green },
  "6_15": { label: "6-15 dias", color: palette.cyan },
  "16_30": { label: "16-30 dias", color: palette.yellow },
  "31_90": { label: "31-90 dias", color: palette.red },
  "90_plus": { label: "+90 dias", color: palette.purple },
} as const;

const ERP = {
  reflected: { label: "Reflejados en ERP", color: "green" },
  pending: { label: "Pendientes por reflejar", color: "yellow" },
  review: { label: "Requieren revision", color: "red" },
  manual: { label: "Registrados manualmente", color: "purple" },
  not_applicable: { label: "Sin sincronizacion", color: "blue" },
} as const;

const SEGMENT = {
  puntual: { label: "Puntual", color: palette.green, badge: "green" },
  tardio: { label: "Tardio", color: palette.yellow, badge: "yellow" },
  variable: { label: "Variable", color: palette.blue, badge: "blue" },
  reincidente: { label: "Reincidente", color: palette.red, badge: "red" },
} as const;

const SIGNAL = {
  mora: { label: "Mora", color: "purple" },
  funds: { label: "Falta de fondos", color: "red" },
  friction: { label: "Friccion recuperable", color: "blue" },
  off_pattern: { label: "Fuera de patron", color: "yellow" },
} as const;

const labelOf = (map: Record<string, { label: string }>, key: string): string =>
  map[key]?.label ?? key;

type PrioritySortKey =
  | "customer"
  | "daysPastDue"
  | "contract"
  | "pendingAmount"
  | "signal";

type PrioritySort = { key: PrioritySortKey; dir: "asc" | "desc" };

/**
 * Filter control for one column, behind a funnel icon: the header stays one
 * line tall, and a column carrying a filter says so by tinting its icon.
 * Portalled because the table scrolls inside a fixed-height box, which would
 * otherwise clip the panel.
 */
const ColumnFilter = ({
  active,
  children,
}: React.PropsWithChildren<{ active: boolean }>): JSX.Element => (
  <Popover.Root>
    <Popover.Trigger asChild>
      <Box
        as="button"
        lineHeight="0"
        color={active ? palette.purple : palette.muted}
        opacity={active ? 1 : 0.45}
        _hover={{ opacity: 1 }}
      >
        <Filter size={11} fill={active ? "currentColor" : "none"} />
      </Box>
    </Popover.Trigger>
    <Portal>
      <Popover.Positioner>
        <Popover.Content w="190px" p={2}>
          {children}
        </Popover.Content>
      </Popover.Positioner>
    </Portal>
  </Popover.Root>
);

/** Column header for the priorities table: the label sorts, the icon filters. */
const PriorityHeader = ({
  label,
  sortKey,
  sort,
  onSort,
  children,
}: React.PropsWithChildren<{
  label: string;
  sortKey: PrioritySortKey;
  sort: PrioritySort;
  onSort: (key: PrioritySortKey) => void;
}>): JSX.Element => {
  const active = sort.key === sortKey;

  return (
    <Flex align="center" gap={1.5}>
      <Flex
        align="center"
        gap={1}
        cursor="pointer"
        userSelect="none"
        color={active ? palette.purple : undefined}
        onClick={() => onSort(sortKey)}
      >
        <Text fontSize="11px" fontWeight="700">
          {label}
        </Text>
        {active ? (
          sort.dir === "asc" ? (
            <ArrowUp size={11} />
          ) : (
            <ArrowDown size={11} />
          )
        ) : (
          <ArrowUpDown size={11} opacity={0.4} />
        )}
      </Flex>
      {children}
    </Flex>
  );
};

/**
 * Body of a help tooltip: an opening paragraph, then optional labelled lines.
 * Kept in one shape so every panel's help reads the same way.
 */
const Help = ({
  intro,
  lines = [],
}: {
  intro: string;
  lines?: string[][];
}): JSX.Element => (
  <Box fontSize="12px" lineHeight="1.5">
    <Text mb={lines.length ? 2 : 0}>{intro}</Text>
    {lines.map(([label, detail]) => (
      <Text key={label} mb={1}>
        <b>{label}:</b> {detail}
      </Text>
    ))}
  </Box>
);

/**
 * The signal names are jargon. Each line says what happened and what to do,
 * in the order the signals are evaluated.
 */
const SIGNAL_HELP = (): JSX.Element => (
  <Help
    intro="Solo se listan inquilinos con saldo pendiente a hoy. Los inquilinos al dia no se incluyen. Los criterios se evaluan en este orden:"
    lines={[
      [
        "Falta de fondos",
        "sus intentos de pago fueron rechazados por saldo o cupo insuficiente. Se recomienda acordar un plan de pago.",
      ],
      [
        "Friccion recuperable",
        "el pago fallo por un error tecnico. Se recomienda reenviar el enlace de pago.",
      ],
      [
        "Fuera de patron",
        "tiene historial de pago puntual y este mes no registra intentos de pago. Se recomienda contacto directo.",
      ],
      ["Mora", "registra saldo vencido sin ninguna de las causas anteriores."],
    ]}
  />
);

export const AnalyticsToolbar = ({
  cutOffPeriod,
  onPeriodChange,
  onRestoreLegacy,
}: {
  cutOffPeriod: string | undefined;
  onPeriodChange: (period: string | undefined) => void;
  onRestoreLegacy: () => void;
}): JSX.Element => (
  <Flex gap={3} align="center">
    <Button
      variant="ghost"
      size="sm"
      color={palette.muted}
      onClick={() => {
        track("analytics_legacy_restored");
        onRestoreLegacy();
      }}
    >
      <ArrowLeft size={14} />
      Volver al resumen anterior
    </Button>
    <Box w="140px">
      <YearMonthPicker
        value={cutOffPeriod}
        setValue={onPeriodChange}
        placeholder="Fecha de corte"
      />
    </Box>
  </Flex>
);

const PanelLoading = (): JSX.Element => (
  <Flex justify="center" align="center" py={10}>
    <Spinner size="md" color={palette.purple} />
  </Flex>
);

export const AnalyticsDashboard = ({
  cutOffPeriod,
}: {
  cutOffPeriod: string | undefined;
}): JSX.Element => {
  const [selectedTenant, setSelectedTenant] = useState<PaywallTenant | null>(
    null
  );
  const [showSettlementDetail, setShowSettlementDetail] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<string | null>(null);
  const [prioritySort, setPrioritySort] = useState<PrioritySort>({
    key: "pendingAmount",
    dir: "desc",
  });
  const [priorityFilters, setPriorityFilters] = useState({
    customer: "",
    contract: "",
    signal: "",
  });
  const segmentCustomers = useAnalyticsSegmentCustomers(
    cutOffPeriod,
    selectedSegment
  );

  const monthly = useAnalyticsMonthly(cutOffPeriod);
  const current = useAnalyticsCurrent();

  const curve = monthly.data?.curve;
  const segments = monthly.data?.segments;
  const erp = monthly.data?.erp;
  const aging = current.data?.aging;
  const priorities = current.data?.priorities;
  const settlement = current.data?.settlement;
  const invoices = monthly.data?.invoices;

  const curveData = (curve?.points ?? []).map((point) => ({
    day: String(point.day),
    real: point.realPct,
    expected: point.expectedPct,
    projection: point.projectionPct,
  }));

  const agingData = (aging?.buckets ?? []).map((bucket) => ({
    bucket: AGING[bucket.key as keyof typeof AGING]?.label ?? bucket.key,
    amount: bucket.amount,
    invoices: bucket.invoices,
    color: AGING[bucket.key as keyof typeof AGING]?.color ?? palette.blue,
  }));

  const segmentData = (segments?.segments ?? [])
    .filter((segment) => segment.customers > 0)
    .map((segment) => ({
      key: segment.key,
      label: SEGMENT[segment.key as keyof typeof SEGMENT].label,
      value: segment.pct,
      color: SEGMENT[segment.key as keyof typeof SEGMENT].color,
    }));

  const toggleSort = (key: PrioritySortKey): void => {
    track("analytics_priorities_table_used", { action: "sort", column: key });
    setPrioritySort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : {
            key,
            dir:
              key === "pendingAmount" || key === "daysPastDue" ? "desc" : "asc",
          }
    );
  };

  const nameOf = (row: { customer: string }): string =>
    row.customer || "Sin nombre";

  const priorityRows = (priorities?.rows ?? [])
    .filter(
      (row) =>
        nameOf(row)
          .toLowerCase()
          .includes(priorityFilters.customer.trim().toLowerCase()) &&
        row.contract
          .toLowerCase()
          .includes(priorityFilters.contract.trim().toLowerCase()) &&
        (!priorityFilters.signal || row.signal === priorityFilters.signal)
    )
    .sort((a, b) => {
      const sign = prioritySort.dir === "asc" ? 1 : -1;
      if (prioritySort.key === "daysPastDue")
        return (a.daysPastDue - b.daysPastDue) * sign;
      if (prioritySort.key === "pendingAmount")
        return (a.pendingAmount - b.pendingAmount) * sign;
      const text = (row: typeof a): string =>
        prioritySort.key === "customer"
          ? nameOf(row)
          : prioritySort.key === "contract"
          ? row.contract
          : labelOf(SIGNAL, row.signal);
      return text(a).localeCompare(text(b)) * sign;
    });

  const textFilter = (field: "customer" | "contract"): JSX.Element => (
    <ColumnFilter active={!!priorityFilters[field]}>
      <Input
        size="xs"
        autoFocus
        placeholder="Filtrar"
        value={priorityFilters[field]}
        onChange={(event) => {
          // Once per filter, on the first character, not per keystroke.
          if (!priorityFilters[field] && event.target.value)
            track("analytics_priorities_table_used", {
              action: "filter",
              column: field,
            });
          setPriorityFilters((current) => ({
            ...current,
            [field]: event.target.value,
          }));
        }}
      />
    </ColumnFilter>
  );

  const kpis = [
    {
      title: "Recaudo a hoy",
      value: money(
        ((curve?.collectedPct ?? 0) / 100) * (curve?.expectedCollection ?? 0)
      ),
      badge:
        curve?.collectedPct != null
          ? `${curve.collectedPct.toFixed(0)}% del esperado`
          : "sin datos",
      note: `${invoices?.paid ?? 0} recaudos`,
      color: palette.blue,
      icon: TrendingUp,
    },
    {
      title: "Proyeccion al cierre",
      value:
        curve?.projectedCloseAmount != null
          ? money(curve.projectedCloseAmount)
          : "—",
      badge:
        curve?.projectedClosePct != null
          ? `${curve.projectedClosePct.toFixed(0)}% del esperado`
          : "mes cerrado",
      color: palette.yellow,
      icon: Gauge,
      help: (
        <Help intro="Estimacion de cuanto se habra recaudado al cerrar el mes si se mantiene el ritmo actual. El calculo toma el avance de hoy y lo compara con el promedio historico para esta misma altura del mes, no con una meta teorica. En meses ya cerrados no se proyecta." />
      ),
    },
    {
      title: "Facturas creadas",
      value: String(invoices?.created ?? 0),
      badge: `${invoices?.pending ?? 0} pendientes`,
      color: palette.purple,
      icon: FilePlus,
      help: (
        <Help intro="Facturas emitidas con vencimiento en el mes seleccionado, sin contar las canceladas. Es el mismo conteo que muestra el Resumen." />
      ),
    },
    {
      title: "Monto vencido",
      value: money(aging?.overdueAmount ?? 0),
      badge: `${aging?.overdueInvoices ?? 0} facturas`,
      color: palette.red,
      icon: AlertTriangle,
    },
    ...(erp?.hasErpActivity
      ? [
          {
            title: "Casos por revisar",
            value: String(erp?.reviewInvoices ?? 0),
            badge: "pagos / ERP",
            color: palette.cyan,
            icon: RefreshCcw,
            help: (
              <Help intro="Pagos recibidos cuyo registro en el ERP quedo en error. El dinero si fue recaudado, pero el ERP no lo refleja, lo que genera diferencias para el equipo contable. Se recomienda revisarlos con el area de sistemas." />
            ),
          },
        ]
      : []),
  ];

  return (
    <Box pb={8}>
      <Grid
        templateColumns={{ base: "1fr", xl: `repeat(${kpis.length}, 1fr)` }}
        gap={4}
        mb={4}
      >
        {kpis.map((kpi) => (
          <KpiCard key={kpi.title} {...kpi} />
        ))}
      </Grid>

      <Grid templateColumns={{ base: "1fr", xl: "1.7fr .9fr" }} gap={4} mb={4}>
        <Panel
          title="Curva de recaudo: real vs esperada"
          subtitle="Compara el avance actual contra el patron historico y proyecta el cierre probable del mes."
          help={
            <Help
              intro="Avance del recaudo dia por dia, como porcentaje de lo facturado en el mes."
              lines={[
                [
                  "Esperada",
                  "promedio de los seis meses anteriores, dia por dia. No es una meta del 100%: refleja hasta donde suele llegar.",
                ],
                [
                  "Real",
                  "lo recaudado hasta hoy. La linea termina en el dia actual, no continua en cero.",
                ],
                ["Proyeccion", "continua desde hoy al ritmo que lleva el mes."],
              ]}
            />
          }
        >
          {monthly.isLoading ? (
            <PanelLoading />
          ) : curveData.length === 0 ? (
            <Text color={palette.muted} fontSize="13px" py={10}>
              Sin facturas en el periodo seleccionado.
            </Text>
          ) : (
            <>
              <Box flex="1" minH="320px">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={curveData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid stroke={palette.border} vertical={false} />
                    <XAxis
                      dataKey="day"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: palette.muted, fontSize: 12 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      // Narrower than Recharts' 60px default to keep the plot
                      // wide, but wide enough for "100%" — a negative left
                      // margin clipped it to "00%".
                      width={44}
                      tick={{ fill: palette.muted, fontSize: 12 }}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Area
                      type="monotone"
                      dataKey="expected"
                      stroke="#CBD5E0"
                      fill="transparent"
                      strokeDasharray="5 5"
                      name="Esperada"
                      connectNulls
                    />
                    <Area
                      type="monotone"
                      dataKey="real"
                      stroke={palette.purple}
                      fill="#3B338D18"
                      strokeWidth={3}
                      name="Real"
                    />
                    <Line
                      type="monotone"
                      dataKey="projection"
                      stroke={palette.yellow}
                      strokeWidth={3}
                      dot={false}
                      name="Proyeccion"
                      connectNulls
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
              <ChartLegend
                items={[
                  ["Esperada", "#CBD5E0"],
                  ["Real", palette.purple],
                  ["Proyeccion", palette.yellow],
                ]}
              />
            </>
          )}
        </Panel>

        <Box display="flex" flexDirection="column" gap={4}>
          <Panel
            title="Segmentos de inquilinos"
            subtitle="Como pagan los inquilinos de la cartera. No es scoring, es comportamiento observable."
            help={
              <Help intro="Clasificacion segun los pagos de los seis meses anteriores al periodo seleccionado. Se requieren al menos 3 facturas pagadas para clasificar a un inquilino, y cada uno aparece en un solo segmento." />
            }
          >
            {monthly.isLoading ? (
              <PanelLoading />
            ) : segmentData.length === 0 ? (
              <Text color={palette.muted} fontSize="13px" py={6}>
                Aun no hay suficiente historial de pagos para segmentar.
              </Text>
            ) : (
              <>
                <Box h="220px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={segmentData}
                        dataKey="value"
                        nameKey="label"
                        innerRadius={48}
                        outerRadius={72}
                        paddingAngle={3}
                        strokeWidth={0}
                        cursor="pointer"
                        onClick={(entry) => {
                          const segment =
                            (entry as { key?: string })?.key ?? null;
                          if (segment)
                            track("analytics_segment_opened", { segment });
                          setSelectedSegment(segment);
                        }}
                      >
                        {segmentData.map((entry) => (
                          <Cell key={entry.label} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <ChartLegend
                  items={segmentData.map((s) => [s.label, s.color])}
                />
                <Text color={palette.muted} fontSize="11px" mt={2}>
                  {segments?.classifiedCustomers ?? 0} inquilinos con historial
                  suficiente. Seleccione un segmento para ver el detalle.
                </Text>
              </>
            )}
          </Panel>

          <Panel
            title="Metodos de Pago"
            subtitle="Como y desde donde pagan los inquilinos este mes."
          >
            {monthly.isLoading ? (
              <PanelLoading />
            ) : (
              <SwitchStatCard
                total={money(curve?.expectedCollection ?? 0)}
                paymentMethods={monthly.data?.byPaymentMethod ?? {}}
                source={monthly.data?.byPaymentSource ?? {}}
              />
            )}
          </Panel>
        </Box>
      </Grid>

      <SectionLabel>Cobranza inteligente</SectionLabel>
      <SectionIntro>
        Solo lo que dificulta el recaudo: errores, fuera de patron y mora real.
        Los buenos pagadores no aparecen aqui porque no requieren gestion.
      </SectionIntro>
      <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap={4} mb={4}>
        <Panel
          title="Prioridades de gestion"
          subtitle="Casos que dificultan el recaudo a hoy. El nombre abre el perfil completo del inquilino."
          help={<SIGNAL_HELP />}
        >
          {current.isLoading ? (
            <PanelLoading />
          ) : (priorities?.rows ?? []).length === 0 ? (
            <Text color={palette.muted} fontSize="13px" py={6}>
              Sin casos que requieran gestion. Toda la cartera pendiente esta
              dentro del patron habitual.
            </Text>
          ) : (
            <>
              <Box
                maxH="420px"
                overflowY="auto"
                css={{
                  "& thead th": {
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 1,
                  },
                }}
              >
                <DataTable
                  headers={[
                    <PriorityHeader
                      key="customer"
                      label="Inquilino"
                      sortKey="customer"
                      sort={prioritySort}
                      onSort={toggleSort}
                    >
                      {textFilter("customer")}
                    </PriorityHeader>,
                    <PriorityHeader
                      key="daysPastDue"
                      label="Vencido"
                      sortKey="daysPastDue"
                      sort={prioritySort}
                      onSort={toggleSort}
                    />,
                    <PriorityHeader
                      key="contract"
                      label="Contrato"
                      sortKey="contract"
                      sort={prioritySort}
                      onSort={toggleSort}
                    >
                      {textFilter("contract")}
                    </PriorityHeader>,
                    <PriorityHeader
                      key="pendingAmount"
                      label="Monto"
                      sortKey="pendingAmount"
                      sort={prioritySort}
                      onSort={toggleSort}
                    />,
                    <PriorityHeader
                      key="signal"
                      label="Señal"
                      sortKey="signal"
                      sort={prioritySort}
                      onSort={toggleSort}
                    >
                      <ColumnFilter active={!!priorityFilters.signal}>
                        <Flex direction="column" gap={0.5}>
                          {[
                            ["", "Todas"],
                            ...Object.entries(SIGNAL).map(
                              ([key, signal]) => [key, signal.label] as const
                            ),
                          ].map(([value, label]) => (
                            <Box
                              as="button"
                              key={value}
                              textAlign="left"
                              fontSize="12px"
                              px={2}
                              py={1}
                              borderRadius="4px"
                              bg={
                                priorityFilters.signal === value
                                  ? palette.soft
                                  : "transparent"
                              }
                              color={
                                priorityFilters.signal === value
                                  ? palette.purple
                                  : palette.label
                              }
                              fontWeight={
                                priorityFilters.signal === value ? "700" : "500"
                              }
                              _hover={{ bg: palette.soft }}
                              onClick={() => {
                                if (value)
                                  track("analytics_priorities_table_used", {
                                    action: "filter",
                                    column: "signal",
                                    value,
                                  });
                                setPriorityFilters((current) => ({
                                  ...current,
                                  signal: value,
                                }));
                              }}
                            >
                              {label}
                            </Box>
                          ))}
                        </Flex>
                      </ColumnFilter>
                    </PriorityHeader>,
                  ]}
                  rows={priorityRows.map((row, index) => [
                    <Text
                      key={`${row.contract}-${index}`}
                      color={palette.purple}
                      fontWeight="600"
                      fontSize="13px"
                      cursor="pointer"
                      _hover={{ textDecoration: "underline" }}
                      onClick={() => {
                        track("analytics_tenant_profile_opened", {
                          source: "priorities",
                          signal: row.signal,
                        });
                        setSelectedTenant({
                          tenant: row.customer || "Sin nombre",
                          contract: row.contract,
                          amount: money(row.pendingAmount),
                          signal: labelOf(SIGNAL, row.signal),
                        });
                      }}
                    >
                      {row.customer || "Sin nombre"}
                    </Text>,
                    // Days overdue travelled in the payload but was never shown.
                    // It carries the urgency the badge no longer encodes, now
                    // that `mora` is the residual signal rather than the first.
                    <Text
                      key={`${row.contract}-${index}-dias`}
                      color={palette.muted}
                      fontSize="11px"
                    >
                      {row.daysPastDue > 0
                        ? `${row.daysPastDue} dias`
                        : "por vencer"}
                    </Text>,
                    row.contract,
                    money(row.pendingAmount),
                    <StatusBadge
                      key={`${row.contract}-${index}-signal`}
                      color={
                        SIGNAL[row.signal as keyof typeof SIGNAL]?.color ??
                        "blue"
                      }
                    >
                      {labelOf(SIGNAL, row.signal)}
                    </StatusBadge>,
                  ])}
                />
              </Box>
              <Text color={palette.muted} fontSize="11px" mt={3}>
                {priorityRows.length !== priorities?.rows.length
                  ? `${priorityRows.length} de ${
                      priorities?.rows.length ?? 0
                    } casos en pantalla.`
                  : priorities?.truncated
                  ? `Mostrando los ${priorities.rows.length} casos de mayor monto de ${priorities.totalQualifying} que requieren gestion.`
                  : `${
                      priorities?.totalQualifying ?? 0
                    } casos requieren gestion.`}
              </Text>
            </>
          )}
        </Panel>

        <Panel
          title="Cartera por edad de mora"
          subtitle="Distribucion de lo pendiente a hoy segun dias de vencimiento, incluyendo meses anteriores."
        >
          {current.isLoading ? (
            <PanelLoading />
          ) : (
            <>
              <Box flex="1" minH="260px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={agingData}
                    layout="vertical"
                    margin={{ top: 8, right: 60, left: 20, bottom: 0 }}
                  >
                    <CartesianGrid stroke={palette.border} horizontal={false} />
                    <XAxis
                      type="number"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: palette.muted, fontSize: 12 }}
                      tickFormatter={(v) => money(Number(v))}
                    />
                    <YAxis
                      type="category"
                      dataKey="bucket"
                      tickLine={false}
                      axisLine={false}
                      width={80}
                      tick={{ fill: palette.muted, fontSize: 12 }}
                    />
                    <Tooltip formatter={(value) => money(Number(value))} />
                    <Bar
                      dataKey="amount"
                      radius={[0, 6, 6, 0]}
                      isAnimationActive={false}
                    >
                      {agingData.map((entry) => (
                        <Cell key={entry.bucket} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <ChartLegend items={agingData.map((d) => [d.bucket, d.color])} />
            </>
          )}
        </Panel>
      </Grid>

      <SectionLabel>Caja y conciliacion</SectionLabel>
      <SectionIntro>
        Distingue lo pagado de lo pendiente y anticipa cuando llega el dinero.
      </SectionIntro>
      <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap={4} mb={4}>
        {erp?.hasErpActivity && (
          <Panel
            title="Pagos en ERP"
            subtitle="Distingue pagos ya reflejados de pagos que pueden generar preguntas del equipo contable."
          >
            <DataTable
              headers={["Estado", "Facturas", "Valor"]}
              rows={(erp?.classes ?? [])
                .filter((row) => row.key !== "not_applicable")
                .map((row) => [
                  <StatusBadge
                    key={row.key}
                    color={ERP[row.key as keyof typeof ERP].color}
                  >
                    {ERP[row.key as keyof typeof ERP].label}
                  </StatusBadge>,
                  row.invoices,
                  money(row.amount),
                ])}
            />
          </Panel>
        )}

        <Panel
          title="Dinero por recibir"
          subtitle="Anticipa cuando el recaudo ya pagado sera consignado."
        >
          {current.isLoading ? (
            <PanelLoading />
          ) : (
            <>
              <MetricRows
                rows={[
                  [
                    "Proximo abono",
                    money(settlement?.nextAmount ?? 0),
                    settlement?.nextSettlementDate
                      ? `estimado el ${settlement.nextSettlementDate}`
                      : "sin abonos pendientes",
                  ],
                  [
                    "Facturas incluidas",
                    String(settlement?.nextInvoices ?? 0),
                    "pagos ya confirmados",
                  ],
                  [
                    "Entra al siguiente ciclo",
                    money(settlement?.laterAmount ?? 0),
                    "pagado despues del corte",
                  ],
                ]}
              />
              <Box
                as="button"
                mt={3}
                w="100%"
                p={3}
                borderRadius="8px"
                bg={palette.soft}
                border="none"
                cursor="pointer"
                _hover={{ bg: "#E8EAF7" }}
                onClick={() => {
                  track("analytics_settlement_detail_opened");
                  setShowSettlementDetail(true);
                }}
              >
                <Flex justify="space-between" align="center">
                  <Text fontSize="13px" fontWeight="700" color={palette.purple}>
                    Ver detalle del abono
                  </Text>
                  <ChevronRight size={16} color={palette.purple} />
                </Flex>
              </Box>
            </>
          )}
        </Panel>
      </Grid>

      <SettlementDrilldownModal
        open={showSettlementDetail}
        onClose={() => setShowSettlementDetail(false)}
        nextSettlementDate={settlement?.nextSettlementDate ?? null}
        nextAmount={settlement?.nextAmount ?? 0}
        nextInvoices={settlement?.nextInvoices ?? 0}
        laterAmount={settlement?.laterAmount ?? 0}
        laterInvoices={settlement?.laterInvoices ?? 0}
        rows={settlement?.detail ?? []}
      />

      <SegmentCustomersModal
        open={!!selectedSegment}
        onClose={() => setSelectedSegment(null)}
        title={
          selectedSegment
            ? SEGMENT[selectedSegment as keyof typeof SEGMENT].label
            : ""
        }
        color={
          selectedSegment
            ? SEGMENT[selectedSegment as keyof typeof SEGMENT].badge
            : "blue"
        }
        loading={segmentCustomers.isLoading}
        rows={segmentCustomers.data?.rows ?? []}
        totalInSegment={segmentCustomers.data?.totalInSegment ?? 0}
        truncated={segmentCustomers.data?.truncated ?? false}
        onOpenProfile={(row) => {
          track("analytics_tenant_profile_opened", {
            source: "segment",
            segment: row.segment,
          });
          // Close this dialog before the paywall opens: two Chakra dialogs
          // stacked leave the scroll lock on <body> when the inner one closes.
          setSelectedSegment(null);
          setSelectedTenant({
            tenant: row.customer || "Sin nombre",
            contract: row.contract,
            amount: money(row.amount),
            signal: SEGMENT[row.segment as keyof typeof SEGMENT].label,
          });
        }}
      />

      <PaywallModal
        open={!!selectedTenant}
        onClose={() => setSelectedTenant(null)}
        tenant={selectedTenant}
      />
    </Box>
  );
};
